/**
 * One comment thread, drawn for either surface.
 *
 * The author workspace and the reviewer page render the same thread with
 * different rights, not different anatomy — so this is one component driven by
 * a small capability object rather than two drifting copies. What a viewer may
 * do (resolve, pick tags) comes in through `caps`; what the thread *is* renders
 * the same everywhere.
 *
 * A resolved thread collapses to a one-line strip until clicked, exactly as in
 * the mockup: it stays in the rail — nothing is lost — but stops occupying the
 * height of a conversation that is over.
 */

import { useMemo, useState } from 'react';
import { api, type ProjectTag, type ThreadWire, type ThreadsWire } from '../../api';
import { Body, Composer, type Mentionable } from '../Composer';
import { formatAgo, mono, monoPlain, RADIUS, REVIEWER_COLOURS } from '../../theme';
import { ds } from '../../ds';

export interface RailCaps {
  theme: 'author' | 'reviewer';
  /** May this viewer resolve or reopen this thread? */
  canResolve: (t: ThreadWire) => boolean;
  /** Shown on a disabled Resolve control, saying whose call it is. */
  resolveDisabledTitle?: string;
  /** Tag chips inside the new-thread composer. */
  tagsInComposer: boolean;
  /** The author's card extras: click-to-cycle tags, assignment, the log. */
  canCycleTag?: boolean;
  canAssign?: boolean;
  showActivity?: boolean;
  /** Estimated card height before the DOM has been measured. */
  estimate: (t: ThreadWire, collapsed: boolean) => number;
  composerEstimate: number;
}

/**
 * One activity row as a sentence. The stored `what` vocabulary is small —
 * started / replied / resolved / reopened / tagged <label> / assigned to
 * <name> / unassigned — and this is the one place it becomes prose.
 */
export function activityLine(what: string, actorName: string): string {
  if (what === 'started') return `${actorName} started this thread`;
  if (what === 'replied') return `${actorName} replied`;
  if (what === 'resolved') return `Resolved by ${actorName}`;
  if (what === 'reopened') return `Reopened by ${actorName}`;
  if (what.startsWith('tagged ')) return `Tagged ${what.slice(7)} by ${actorName}`;
  if (what.startsWith('assigned to ')) return `${what[0]!.toUpperCase()}${what.slice(1)} by ${actorName}`;
  if (what === 'unassigned') return `Unassigned by ${actorName}`;
  return `${actorName} ${what}`;
}

/** Initials for an identity chip: first letters of the first two words. */
export function initialsFor(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]!.toUpperCase())
      .join('') || '?'
  );
}

/** The paired ink for an identity colour, from the fixed suite ramp. */
export function inkFor(bg: string): string {
  const known = REVIEWER_COLOURS.find((c) => c.bg.toLowerCase() === bg.toLowerCase());
  return known?.ink ?? '#f3f5fe';
}

export function Avatar({ name, colour, size = 18 }: { name: string; colour: string; size?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        background: colour,
        color: inkFor(colour),
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        font: `700 ${Math.round(size * 0.44)}px/1 Inter, system-ui, sans-serif`,
        letterSpacing: '.02em',
        flex: 'none'
      }}
    >
      {initialsFor(name)}
    </span>
  );
}

export function ThreadCard({
  thread,
  people,
  tags,
  me,
  projectId,
  caps,
  selected,
  carriedOver,
  collapsed,
  replying,
  guardWrite,
  onToggleCollapsed,
  onReply,
  onChanged,
  onSelect
}: {
  thread: ThreadWire;
  people: Mentionable[];
  tags: ProjectTag[];
  me: ThreadsWire['me'] | null;
  projectId: string;
  caps: RailCaps;
  selected: boolean;
  /** Open since an earlier round — the amber chip the rounds page counts. */
  carriedOver?: boolean;
  /** Resolved threads collapse; the rail owns which are expanded again. */
  collapsed: boolean;
  replying: boolean;
  /** Gate for writes; returns false after opening whatever dialog it needs. */
  guardWrite?: () => boolean;
  onToggleCollapsed: () => void;
  onReply: () => void;
  onChanged: () => void;
  onSelect: () => void;
}) {
  const { Icon } = ds();
  const tag = tags.find((t) => t.id === thread.tagId);
  const resolved = !!thread.resolvedAt;
  const [assignOpen, setAssignOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const personFor = useMemo(
    () => (key: string) => people.find((p) => p.key === key),
    [people]
  );
  const assignee = thread.assignedTo ? personFor(thread.assignedTo) : undefined;

  /** The mockup's chip cycles through the set on click — no menu to open. */
  const cycleTag = () => {
    if (!tags.length) return;
    const idx = tags.findIndex((t) => t.id === thread.tagId);
    const next = tags[(idx + 1) % tags.length]!;
    api.threads
      .setTag(projectId, thread.id, next.id)
      .catch(() => undefined)
      .finally(onChanged);
  };

  if (collapsed) {
    const first = thread.messages[0];
    const excerpt = first?.body ?? '';
    return (
      <div
        data-card={thread.id}
        onClick={onToggleCollapsed}
        style={{
          padding: '10px 12px',
          borderRadius: RADIUS.card,
          cursor: 'pointer',
          background: 'var(--surface-sunken, transparent)',
          border: '1px solid var(--border-subtle)',
          opacity: 0.85
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
          <Icon name="check-circle" size={13} style={{ color: 'var(--signal)', flex: 'none' }} />
          <span style={{ ...mono, fontSize: 9, color: 'var(--signal)' }}>
            RESOLVED{thread.resolvedBy ? ` BY ${thread.resolvedBy.split(' ')[0]!.toUpperCase()}` : ''}
            {thread.resolvedAt ? ` · ${formatAgo(thread.resolvedAt)}` : ''}
          </span>
          <span style={{ flex: 1 }} />
          {tag && (
            <span style={{ ...mono, fontSize: 8, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
              {tag.label.toUpperCase()}
            </span>
          )}
          <Icon name="chevron-down" size={12} style={{ color: 'var(--text-muted)' }} />
        </div>
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-muted)',
            lineHeight: 1.45,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {excerpt}
        </div>
      </div>
    );
  }

  return (
    <div
      data-card={thread.id}
      onClick={onSelect}
      style={{
        padding: 12,
        borderRadius: RADIUS.card,
        cursor: 'pointer',
        background: resolved ? 'var(--surface-sunken, transparent)' : 'var(--surface-card)',
        border: `1px solid ${selected ? 'var(--primary)' : 'var(--border-subtle)'}`,
        boxShadow: selected ? '0 0 0 3px var(--primary-soft)' : 'none',
        opacity: resolved ? 0.72 : 1
      }}
    >
      {thread.anchorLabel && (
        <p style={{ ...mono, fontSize: 8.5, marginBottom: 6, lineHeight: 1.5 }}>
          {thread.anchorLabel}
        </p>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        {caps.canCycleTag && tags.length > 0 ? (
          <button
            type="button"
            title="Click to change the tag"
            onClick={(e) => {
              e.stopPropagation();
              cycleTag();
            }}
            style={{
              ...mono,
              fontSize: 9,
              color: tag?.colour ?? 'var(--text-muted)',
              background: 'none',
              border: '1px solid var(--border-subtle)',
              borderRadius: RADIUS.chip,
              padding: '2px 7px',
              cursor: 'pointer'
            }}
          >
            {(tag?.label ?? 'tag').toUpperCase()}
          </button>
        ) : (
          tag && (
            <span style={{ ...mono, fontSize: 9, color: tag.colour }}>
              {tag.label.toUpperCase()}
            </span>
          )
        )}
        {carriedOver && (
          <span
            style={{
              ...mono,
              fontSize: 8,
              padding: '2px 6px',
              borderRadius: RADIUS.chip,
              color: '#e8a33d',
              border: '1px solid #e8a33d'
            }}
          >
            ROUND {thread.round}
          </span>
        )}
        {assignee && (
          <span
            style={{
              ...mono,
              fontSize: 8.5,
              padding: '2px 7px',
              borderRadius: RADIUS.chip,
              background: 'rgba(136,97,147,.22)',
              color: '#cbb0d1'
            }}
          >
            {assignee.name.split(' ')[0]}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {resolved && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapsed();
            }}
            style={{ ...mono, fontSize: 9, color: 'var(--signal)', background: 'none', border: 0, cursor: 'pointer' }}
          >
            RESOLVED <Icon name="chevron-up" size={10} />
          </button>
        )}
      </div>

      {thread.messages.map((m) => {
        const author = personFor(m.authorKey);
        return (
          <div key={m.id} style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
              <Avatar name={author?.name ?? '?'} colour={author?.colour ?? '#796cbf'} size={16} />
              <span style={{ ...monoPlain, fontSize: 10 }}>
                {author?.name ?? 'Someone'}
                {me && m.authorKey === me.key ? ' · you' : ''} · {formatAgo(m.createdAt)}
              </span>
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--text-body)' }}>
              <Body text={m.body} people={people} meKey={me?.key ?? ''} />
            </div>
            {m.attachments.map((a) => (
              <a
                key={a.id}
                href={`/api/review/projects/${projectId}/attachments/${a.id}`}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ display: 'block', marginTop: 6 }}
              >
                <img
                  src={`/api/review/projects/${projectId}/attachments/${a.id}`}
                  alt="Attachment"
                  loading="lazy"
                  style={{
                    maxWidth: '100%',
                    maxHeight: 160,
                    borderRadius: RADIUS.control,
                    border: '1px solid var(--border-subtle)',
                    display: 'block'
                  }}
                />
              </a>
            ))}
          </div>
        );
      })}

      <div style={{ display: 'flex', gap: 6, marginTop: 10, alignItems: 'center' }}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (guardWrite && !guardWrite()) return;
            api.threads
              .react(projectId, thread.id)
              .catch(() => undefined)
              .finally(onChanged);
          }}
          style={{
            ...mono,
            fontSize: 9.5,
            padding: '4px 8px',
            borderRadius: RADIUS.chip,
            cursor: 'pointer',
            border: `1px solid ${thread.reacted ? 'var(--signal)' : 'var(--border-subtle)'}`,
            background: 'transparent',
            color: thread.reacted ? 'var(--signal)' : 'var(--text-muted)'
          }}
        >
          <Icon name="check" size={11} /> {thread.reactions || 'Agree'}
        </button>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onReply();
          }}
          style={{ ...mono, fontSize: 9.5, background: 'none', border: 0, color: 'var(--text-muted)', cursor: 'pointer' }}
        >
          Reply
        </button>

        {caps.showActivity && thread.activity.length > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setActivityOpen((o) => !o);
            }}
            style={{ ...mono, fontSize: 9.5, background: 'none', border: 0, color: activityOpen ? 'var(--primary)' : 'var(--text-muted)', cursor: 'pointer' }}
          >
            Activity
          </button>
        )}

        {caps.canAssign && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setAssignOpen((o) => !o);
            }}
            style={{ ...mono, fontSize: 9.5, background: 'none', border: 0, color: assignOpen ? 'var(--primary)' : 'var(--text-muted)', cursor: 'pointer' }}
          >
            Assign
          </button>
        )}

        <span style={{ flex: 1 }} />

        {/* Whose call resolution is depends on the surface: the author's for
            everything, a reviewer's for their own threads. When it is not this
            viewer's call the control is shown and disabled rather than hidden
            — the mockup's choice, so the affordance is learnable. */}
        <button
          type="button"
          disabled={!caps.canResolve(thread)}
          title={caps.canResolve(thread) ? undefined : caps.resolveDisabledTitle}
          onClick={(e) => {
            e.stopPropagation();
            api.threads
              .resolve(projectId, thread.id, !resolved)
              .catch(() => undefined)
              .finally(onChanged);
          }}
          style={{
            ...mono,
            fontSize: 9.5,
            background: 'none',
            border: 0,
            cursor: caps.canResolve(thread) ? 'pointer' : 'default',
            color: resolved ? 'var(--signal)' : 'var(--text-muted)'
          }}
        >
          {resolved ? 'Reopen' : 'Resolve'}
        </button>
      </div>

      {assignOpen && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            marginTop: 8,
            padding: 6,
            borderRadius: RADIUS.control,
            border: '1px solid var(--border-subtle)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4
          }}
        >
          {people.map((pn) => (
            <button
              key={pn.key}
              type="button"
              onClick={() => {
                setAssignOpen(false);
                api.threads
                  .assign(projectId, thread.id, pn.key === thread.assignedTo ? null : pn.key)
                  .catch(() => undefined)
                  .finally(onChanged);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 11.5,
                padding: '3px 8px',
                borderRadius: RADIUS.chip,
                cursor: 'pointer',
                border: `1px solid ${pn.key === thread.assignedTo ? 'var(--primary)' : 'var(--border-subtle)'}`,
                background: 'none',
                color: 'var(--text-body)'
              }}
            >
              <Avatar name={pn.name} colour={pn.colour} size={13} />
              {pn.name.split(' ')[0]}
            </button>
          ))}
        </div>
      )}

      {activityOpen && caps.showActivity && (
        <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 8 }}>
          <p style={{ ...mono, fontSize: 8.5, margin: '0 0 4px' }}>Activity</p>
          {thread.activity.map((a, i) => (
            <div
              key={i}
              style={{ display: 'flex', gap: 6, alignItems: 'baseline', padding: '2px 0' }}
            >
              <span aria-hidden="true" style={{ color: 'var(--text-muted)', fontSize: 10 }}>·</span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', flex: 1 }}>
                {activityLine(a.what, a.actorName)}
              </span>
              <span style={{ ...monoPlain, fontSize: 8.5, whiteSpace: 'nowrap' }}>
                {formatAgo(a.createdAt)}
              </span>
            </div>
          ))}
        </div>
      )}

      {replying && me && (
        <div style={{ marginTop: 10 }} onClick={(e) => e.stopPropagation()}>
          <Composer
            people={people}
            tags={[]}
            placeholder="Reply. @ to tag someone."
            submitLabel="Reply"
            projectId={projectId}
            onCancel={onReply}
            onSubmit={async (body, mentions, _tagId, attachmentIds) => {
              await api.threads.reply(projectId, thread.id, body, mentions, attachmentIds);
              onReply();
              onChanged();
            }}
          />
        </div>
      )}
    </div>
  );
}
